import { useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';

/**
 * ChatbaseLoader - Chatbase chatbot widget component
 * 
 * React-friendly implementation using Tailwind CSS
 * Layout-aware positioning for PublicLayout and DashboardLayout
 * 
 * @param {Object} props - Component props
 * @param {string} props.layoutType - Layout type: 'public' or 'dashboard' (default: 'public')
 * @returns {JSX.Element|null} ChatbaseLoader component
 */
const ChatbaseLoader = ({ layoutType = 'public' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const isPublicLayout = layoutType === 'public';

  // Toggle chat window
  const handleToggle = () => {
    setIsOpen(prev => !prev);
  };

  // Container position classes based on layout
  const containerClasses = isPublicLayout
    ? 'fixed bottom-[100px] right-10 z-[1031] md:bottom-[100px] md:right-10'
    : 'fixed bottom-8 right-8 z-[1031] md:bottom-8 md:right-8';

  // Chat window position classes based on layout
  const chatWindowClasses = isPublicLayout
    ? 'fixed bottom-[160px] right-10 w-[350px] h-[500px] md:bottom-[160px] md:right-10 md:w-[350px] md:h-[500px]'
    : 'fixed bottom-[100px] right-8 w-[350px] h-[500px] md:bottom-[100px] md:right-8 md:w-[350px] md:h-[500px]';

  // Mobile responsive classes
  const mobileClasses = 'max-md:bottom-[100px] max-md:right-10 max-md:w-[300px] max-md:h-[450px]';
  const mobileSmallClasses = 'max-[380px]:w-[280px] max-[380px]:right-[65px]';

  const chatWindowMobileClasses = isPublicLayout
    ? `${mobileClasses} max-md:bottom-[150px] max-md:right-10`
    : `${mobileClasses} max-md:bottom-20 max-md:right-5`;

  const chatWindowMobileSmallClasses = isPublicLayout
    ? `${mobileSmallClasses} max-[380px]:right-[65px]`
    : `${mobileSmallClasses} max-[380px]:right-[10px]`;

  // Render to body using portal to avoid CSS conflicts
  return createPortal(
    <div className={containerClasses}>
      {/* Chat Button */}
      <button
        onClick={handleToggle}
        className="cursor-pointer flex items-center justify-center transition-transform hover:scale-110 focus:outline-none rounded-full"
        aria-label="Chat'i aç/kapat"
        type="button"
      >
        <img
          src="/img/floating_button_logo.png"
          alt="Chat"
          className="w-[65px] h-[65px] object-contain max-md:w-12 max-md:h-12"
        />
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div
          className={`${chatWindowClasses} ${chatWindowMobileClasses} ${chatWindowMobileSmallClasses} bg-white rounded-lg shadow-lg overflow-hidden z-[1031]`}
        >
          <iframe
            src="https://www.chatbase.co/chatbot-iframe/CrLGTTpwdyzsuMK9ieiFj?lang=tr"
            className="w-full h-full border-none"
            title="Chatbase Chatbot"
            allow="microphone"
          />
        </div>
      )}
    </div>,
    document.body
  );
};

ChatbaseLoader.propTypes = {
  layoutType: PropTypes.oneOf(['public', 'dashboard']),
};

export default ChatbaseLoader;